/**
 * Engagement agreements (S-A31) — the list.
 *
 * ===========================================================================
 * WHY THERE IS NO CLIENT NAME IN THE COUNTERPARTY COLUMN
 * ===========================================================================
 * `AgreementListSerializer` returns `client` and `lead` as bare identifiers. There is
 * no denormalised `client_name`, the way `CaseListItem` carries one, so a name would
 * have to come from a second request.
 *
 * The tempting fix — fetch the first page of clients and build a lookup — is wrong in
 * a way that only shows up on a real firm: the map covers whoever happened to be on
 * page one, and every agreement for client 101 onwards silently renders blank. A
 * column that is right for a demo and blank in production teaches people to distrust
 * the screen.
 *
 * So the counterparty is rendered as what the payload actually holds: a labelled link
 * to the record, with the KIND said in words, because "lead" and "client" are not
 * interchangeable — an agreement to a lead is pre-conversion, and that is the single
 * most useful thing this column can tell somebody scanning. The detail screen resolves
 * the real name from the one record it is already opening.
 *
 * This is raised in the report as a backend change worth making (`client_name` /
 * `lead_name` on the list serializer), not worked around here.
 * ===========================================================================
 *
 * `status` is server-side and REPEATABLE, and the backend rejects an unknown value
 * with a 400 rather than ignoring it — so the options below mirror `Agreement.Status`
 * exactly.
 */

import {
  STAFF,
  qk,
  usePaginatedList,
  type AgreementListItem,
  type AgreementStatus,
} from '@legalcrm/api';
import { Badge, Input, Select, StatusPill, Table, type Column } from '@legalcrm/ui';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { formatDate, humanise } from '@/lib/format';
import { PageHeader } from '@/shell/PageHeader';

/** Mirrors `Agreement.Status` in `domains/agreements/models.py`. */
const STATUS_OPTIONS = [
  { value: '', label: 'Any status' },
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent, awaiting signature' },
  { value: 'viewed', label: 'Viewed' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'signed', label: 'Signed' },
  { value: 'active', label: 'Active' },
  { value: 'superseded', label: 'Superseded' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'expired', label: 'Expired' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'terminated', label: 'Terminated' },
];

/**
 * Tone is stated here rather than taken from `statusTone()`, which covers the statuses
 * the KERNEL defines. An agreement's lifecycle is its own, and "active" meaning
 * "countersigned and in force" is not the same "active" as anywhere else.
 */
export function agreementTone(status: AgreementStatus) {
  switch (status) {
    case 'active':
    case 'signed':
    case 'accepted':
      return 'success' as const;
    case 'sent':
    case 'viewed':
      return 'info' as const;
    case 'rejected':
    case 'expired':
    case 'terminated':
      return 'danger' as const;
    case 'draft':
    case 'superseded':
    case 'cancelled':
      return 'neutral' as const;
    default:
      return 'neutral' as const;
  }
}

export function AgreementListPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const list = usePaginatedList<AgreementListItem>(qk.list('agreements'), STAFF.agreements, {
    status: status || undefined,
    search: debounced || undefined,
  });

  const { resetCursor } = list;
  useEffect(() => {
    resetCursor();
  }, [status, debounced, resetCursor]);

  const columns: Column<AgreementListItem>[] = [
    {
      key: 'title',
      header: 'Agreement',
      render: (row) => (
        <div className="min-w-0">
          {/* A real link, so middle-click and "open in new tab" work on a row that
              is also clickable as a whole. */}
          <Link
            to={`/agreements/${row.id}`}
            onClick={(event) => event.stopPropagation()}
            className="font-medium text-primary hover:underline"
          >
            {row.title}
          </Link>
          {row.version_number > 1 && (
            <p className="text-xs text-ink-muted">Version {row.version_number}</p>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: 'w-40',
      render: (row) => <StatusPill label={humanise(row.status)} tone={agreementTone(row.status)} />,
    },
    {
      key: 'counterparty',
      header: 'Counterparty',
      render: (row) => <CounterpartyLink agreement={row} />,
    },
    {
      key: 'sent_at',
      header: 'Sent',
      align: 'right',
      render: (row) =>
        row.sent_at ? (
          <span className="tabular-nums">{formatDate(row.sent_at)}</span>
        ) : (
          <Badge tone="neutral" size="sm">
            Not sent
          </Badge>
        ),
    },
    {
      key: 'signed_at',
      header: 'Signed',
      align: 'right',
      render: (row) =>
        row.signed_at ? (
          <span className="tabular-nums">{formatDate(row.signed_at)}</span>
        ) : (
          <span className="text-ink-subtle">—</span>
        ),
    },
    {
      key: 'expires_at',
      header: 'Expires',
      align: 'right',
      hideOnMobile: true,
      render: (row) =>
        row.expires_at ? (
          <span className="tabular-nums text-ink-muted">{formatDate(row.expires_at)}</span>
        ) : (
          <span className="text-ink-subtle">—</span>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Agreements"
        description="Engagement letters and fee arrangements, and where each one has got to."
      />

      <div className="mb-3 grid gap-2 sm:grid-cols-2 lg:w-2/3">
        <Input
          label="Search"
          hideLabel
          type="search"
          placeholder="Search by title"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <Select
          label="Status"
          hideLabel
          options={STATUS_OPTIONS}
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        />
      </div>

      <Table
        caption="Agreements"
        columns={columns}
        rows={list.items}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        error={list.error}
        onRetry={() => void list.refetch()}
        onRowClick={(row) => navigate(`/agreements/${row.id}`)}
        emptyTitle="No agreements"
        // Never speculates about permissions: an empty result may mean the firm has
        // none, or that they are all outside this reader's scope, and the API answers
        // those two identically on purpose.
        emptyDescription="Nothing matches these filters."
        pagination={{
          hasNext: list.hasNext,
          hasPrevious: list.hasPrevious,
          onNext: list.goNext,
          onPrevious: list.goPrevious,
          summary: `${list.items.length} shown`,
        }}
      />
    </>
  );
}

/**
 * The counterparty, as a kind plus a link.
 *
 * The check constraint guarantees exactly one of the two is set, but this renders the
 * "neither" case rather than asserting it: a row that violated the constraint would be
 * a data problem worth seeing, not worth crashing on.
 */
function CounterpartyLink({ agreement }: { agreement: AgreementListItem }) {
  if (agreement.client) {
    return (
      <Link
        to={`/clients/${agreement.client}`}
        onClick={(event) => event.stopPropagation()}
        className="inline-flex items-center gap-2 text-primary hover:underline"
      >
        <Badge tone="neutral" size="sm">
          Client
        </Badge>
        Open record
      </Link>
    );
  }
  if (agreement.lead) {
    return (
      <Link
        to={`/leads/${agreement.lead}`}
        onClick={(event) => event.stopPropagation()}
        className="inline-flex items-center gap-2 text-primary hover:underline"
      >
        {/* Worth saying out loud: this agreement went out before conversion. */}
        <Badge tone="info" size="sm">
          Lead
        </Badge>
        Open record
      </Link>
    );
  }
  return <span className="text-ink-subtle">—</span>;
}
